import { useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Loader2, Upload, X, FileText, Download, Sparkles, RefreshCw } from "lucide-react";

// Enum standard 4 livelli (ottimo/buono/sufficiente/critico) usato ovunque
export const STATO_ENUM = [
  ["ottimo", "Ottimo"],
  ["buono", "Buono"],
  ["sufficiente", "Sufficiente"],
  ["critico", "Critico"],
];

export const AUTONOMIA_ENUM = [
  ["alta", "Alta"],
  ["media", "Media"],
  ["bassa", "Bassa"],
];

export const LAVANDERIA_STATO_ENUM = [
  ["regolare", "Regolare"],
  ["sufficiente", "Sufficiente"],
  ["critico", "Critico"],
];

// ---------- Field primitives ----------
export function FieldNum({ label, k, data, setData, step, disabled, suf, testid }) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">
        {label}
        {suf && <span className="text-muted-foreground/70 normal-case tracking-normal"> ({suf})</span>}
      </label>
      <Input
        type="number"
        step={step || "any"}
        value={data[k] ?? ""}
        disabled={disabled}
        onChange={(e) => setData({ ...data, [k]: e.target.value === "" ? null : parseFloat(e.target.value) })}
        data-testid={testid || `field-${k}`}
      />
    </div>
  );
}

export function FieldText({ label, k, data, setData, disabled, area, testid, className }) {
  return (
    <div className={`space-y-1.5 md:col-span-2 ${className || ""}`}>
      <label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">{label}</label>
      {area ? (
        <Textarea
          value={data[k] ?? ""}
          disabled={disabled}
          onChange={(e) => setData({ ...data, [k]: e.target.value })}
          className="min-h-[80px]"
          data-testid={testid || `field-${k}`}
        />
      ) : (
        <Input
          value={data[k] ?? ""}
          disabled={disabled}
          onChange={(e) => setData({ ...data, [k]: e.target.value })}
          data-testid={testid || `field-${k}`}
        />
      )}
    </div>
  );
}

export function FieldSelect({ label, k, data, setData, options, disabled, placeholder, testid }) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">{label}</label>
      <Select value={data[k] ?? ""} onValueChange={(v) => setData({ ...data, [k]: v })} disabled={disabled}>
        <SelectTrigger data-testid={testid || `field-${k}`}>
          <SelectValue placeholder={placeholder || "Seleziona…"} />
        </SelectTrigger>
        <SelectContent>
          {options.map(([val, lab]) => (
            <SelectItem key={val} value={val}>{lab}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

export function FieldStato({ label, k, data, setData, disabled, testid }) {
  return <FieldSelect label={label} k={k} data={data} setData={setData} options={STATO_ENUM} disabled={disabled} testid={testid} />;
}

export function FieldBool({ label, k, data, setData, disabled, testid }) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">{label}</label>
      <Select
        value={data[k] === true ? "si" : data[k] === false ? "no" : ""}
        onValueChange={(v) => setData({ ...data, [k]: v === "si" })}
        disabled={disabled}
      >
        <SelectTrigger data-testid={testid || `field-${k}`}>
          <SelectValue placeholder="—" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="si">Sì</SelectItem>
          <SelectItem value="no">No</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}

// ---------- Target badge (rosso/verde) ----------
// higherIsBetter=true → value >= target = emerald, altrimenti red-500.
// Se target o value null/undefined → non renderizza colore.
export function TargetHint({ value, target, higherIsBetter = true, unit }) {
  if (value == null || value === "" || target == null || target === "") return null;
  const v = parseFloat(value);
  const t = parseFloat(target);
  if (Number.isNaN(v) || Number.isNaN(t)) return null;
  const met = higherIsBetter ? v >= t : v <= t;
  return (
    <span
      className={`text-[10px] tabular-nums font-medium ${met ? "text-emerald-500" : "text-red-500"}`}
      data-testid="target-hint"
    >
      {met ? "✓" : "✗"} target {t}{unit || ""}
    </span>
  );
}

export function FieldNumWithTarget({ label, k, data, setData, step, disabled, suf, target, higherIsBetter = true, unit, testid }) {
  const v = data[k];
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between gap-2">
        <label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">
          {label}
          {suf && <span className="text-muted-foreground/70 normal-case tracking-normal"> ({suf})</span>}
        </label>
        {target != null && <TargetHint value={v} target={target} higherIsBetter={higherIsBetter} unit={unit} />}
      </div>
      <Input
        type="number"
        step={step || "any"}
        value={v ?? ""}
        disabled={disabled}
        onChange={(e) => setData({ ...data, [k]: e.target.value === "" ? null : parseFloat(e.target.value) })}
        data-testid={testid || `field-${k}`}
      />
    </div>
  );
}

// ---------- Attachments panel ----------
const ACCEPT = ".pdf,.jpg,.jpeg,.png,.webp,.gif,.xlsx,.xls,.csv,image/*,application/pdf";
const MAX_SIZE = 10 * 1024 * 1024;

export function AttachmentsPanel({ reportId, section, attachments, onChange, isReadOnly }) {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const sectionAtts = (attachments || []).filter((a) => a.section === section);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_SIZE) {
      toast.error("File troppo grande (max 10 MB)");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("section", section);
      const { data } = await api.post(`/monthly-reports/${reportId}/attachments`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const next = [...(attachments || []), {
        id: data.id, section, filename: data.filename,
        content_type: file.type, uploaded_at: new Date().toISOString(),
      }];
      onChange?.(next);
      toast.success(`Caricato: ${data.filename}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const remove = async (attId) => {
    try {
      await api.delete(`/monthly-reports/${reportId}/attachments/${attId}`);
      onChange?.((attachments || []).filter((a) => a.id !== attId));
      toast.success("Allegato rimosso");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const download = async (att) => {
    try {
      const res = await api.get(`/monthly-reports/${reportId}/attachments/${att.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = att.filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="mt-4 pt-4 border-t border-border/60" data-testid={`attachments-${section}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
          Allegati sezione ({sectionAtts.length})
        </div>
        {!isReadOnly && (
          <>
            <input
              ref={fileRef}
              type="file"
              accept={ACCEPT}
              onChange={handleFile}
              className="hidden"
              data-testid={`upload-input-${section}`}
            />
            <Button
              size="sm"
              variant="outline"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              data-testid={`upload-btn-${section}`}
            >
              {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Carica file
            </Button>
          </>
        )}
      </div>
      {sectionAtts.length === 0 ? (
        <p className="text-xs text-muted-foreground text-center py-3">
          Nessun allegato. Formati: pdf, immagini, xlsx, csv. Max 10 MB.
        </p>
      ) : (
        <ul className="space-y-1.5">
          {sectionAtts.map((a) => (
            <li key={a.id} className="flex items-center gap-2 text-sm rounded-lg bg-accent/30 px-3 py-1.5" data-testid={`att-item-${a.id}`}>
              <FileText className="h-3.5 w-3.5 text-lavender-600 shrink-0" />
              <button className="flex-1 truncate text-left hover:underline" onClick={() => download(a)}>
                {a.filename}
              </button>
              <Button size="sm" variant="ghost" onClick={() => download(a)} title="Scarica">
                <Download className="h-3.5 w-3.5" />
              </Button>
              {!isReadOnly && (
                <Button size="sm" variant="ghost" onClick={() => remove(a.id)} className="text-rose-500" title="Rimuovi">
                  <X className="h-3.5 w-3.5" />
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ---------- AI Draft Panel ----------
// Bottone Genera con AI per sezione. Se già presente ai_commento mostra:
//  - Card con testo AI editabile
//  - Bottone "Rigenera" con conferma modal
// La textarea del commento AI viene salvata via `setData` (chiave `ai_commento`).
export function AIDraftPanel({ reportId, section, data, setData, isReadOnly, onGenerated }) {
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const existing = data?.ai_commento;
  const generatedAt = data?.ai_generated_at;
  const edited = data?.ai_edited;

  const run = async () => {
    setBusy(true);
    setConfirmOpen(false);
    try {
      const { data: r } = await api.post(`/monthly-reports/${reportId}/ai/generate-section`, { section });
      setData({
        ...data,
        ai_commento: r.ai_draft,
        ai_generated_at: r.generated_at,
        ai_edited: false,
      });
      toast.success("Bozza AI generata");
      onGenerated?.(r);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-4 pt-4 border-t border-border/60" data-testid={`ai-draft-${section}`}>
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.16em] text-lavender-600">
          <Sparkles className="h-3.5 w-3.5" />
          Bozza AI
          {existing && (
            <span className={`text-[9px] normal-case tracking-normal px-2 py-0.5 rounded-full font-medium ${
              edited
                ? "bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-200"
                : "bg-lavender-100 text-lavender-700 dark:bg-lavender-950/40 dark:text-lavender-300"
            }`}>
              {edited ? "Modificato dal manager" : "Bozza AI editabile"}
            </span>
          )}
          {generatedAt && (
            <span className="text-[9px] normal-case tracking-normal text-muted-foreground">
              {new Date(generatedAt).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
            </span>
          )}
        </div>
        {!isReadOnly && (
          existing ? (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setConfirmOpen(true)}
              disabled={busy}
              data-testid={`ai-regen-${section}`}
            >
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Rigenera con AI
            </Button>
          ) : (
            <Button
              size="sm"
              onClick={run}
              disabled={busy}
              className="bg-lavender-500 hover:bg-lavender-600 text-white"
              data-testid={`ai-generate-${section}`}
            >
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} Genera con AI
            </Button>
          )
        )}
      </div>

      {existing && (
        <Textarea
          value={existing}
          disabled={isReadOnly}
          onChange={(e) => setData({ ...data, ai_commento: e.target.value, ai_edited: true })}
          className="min-h-[140px] text-sm bg-lavender-50/40 dark:bg-lavender-950/10 border-lavender-200/60"
          placeholder="Il testo generato dall'AI apparirà qui."
          data-testid={`ai-textarea-${section}`}
        />
      )}
      {!existing && !busy && !isReadOnly && (
        <p className="text-xs text-muted-foreground">
          Compila i dati numerici della sezione poi clicca &quot;Genera con AI&quot; per ottenere una bozza testuale professionale confrontata con mese precedente e obiettivi hotel.
        </p>
      )}

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="sm:max-w-md" data-testid={`ai-regen-modal-${section}`}>
          <DialogHeader>
            <DialogTitle>Rigenera con AI</DialogTitle>
            <DialogDescription>
              La bozza AI corrente verrà sovrascritta con una nuova generazione. Il testo attuale andrà perso. Confermi?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>Annulla</Button>
            <Button onClick={run} disabled={busy} data-testid={`ai-regen-confirm-${section}`}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Rigenera
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ---------- AI Conclusioni Panel (usato solo in sezione generale) ----------
export function AIConclusioniPanel({ reportId, data, setData, isReadOnly }) {
  const [busy, setBusy] = useState(false);
  const existing = data?.ai_conclusioni;
  const generatedAt = data?.ai_conclusioni_generated_at;

  const run = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post(`/monthly-reports/${reportId}/ai/generate-conclusioni`);
      setData({
        ...data,
        ai_conclusioni: r.ai_conclusioni,
        ai_conclusioni_generated_at: r.generated_at,
      });
      toast.success("Conclusioni generate");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-4 pt-4 border-t border-border/60 space-y-2 md:col-span-2" data-testid="ai-conclusioni">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.16em] text-lavender-600">
          <Sparkles className="h-3.5 w-3.5" />
          Conclusioni e priorità del mese successivo
          {generatedAt && (
            <span className="text-[9px] normal-case tracking-normal text-muted-foreground">
              generate il {new Date(generatedAt).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
            </span>
          )}
        </div>
        {!isReadOnly && (
          <Button
            size="sm"
            onClick={run}
            disabled={busy}
            className={existing ? "" : "bg-lavender-500 hover:bg-lavender-600 text-white"}
            variant={existing ? "outline" : "default"}
            data-testid="ai-conclusioni-btn"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : existing ? <RefreshCw className="h-3.5 w-3.5" /> : <Sparkles className="h-3.5 w-3.5" />}
            {existing ? " Rigenera" : " Genera conclusioni con AI"}
          </Button>
        )}
      </div>
      <Textarea
        value={existing ?? ""}
        disabled={isReadOnly}
        onChange={(e) => setData({ ...data, ai_conclusioni: e.target.value })}
        className="min-h-[180px] text-sm bg-lavender-50/40 dark:bg-lavender-950/10 border-lavender-200/60"
        placeholder="Sintesi conclusiva del mese e priorità per il mese successivo. Puoi generarla con AI o scriverla a mano."
        data-testid="ai-conclusioni-textarea"
      />
    </div>
  );
}

